import { morph } from '@theme/morph';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { StandardEvents } from '@shopify/events';

/**
 * Page number that asks `blocks/gco-filters.liquid` for the drawer payload instead of the full block.
 * It is far beyond the last page of any collection, so the response also carries no product cards.
 * Keep in sync with the `current_page == 999999` check in `blocks/gco-filters.liquid`.
 */
export const DRAWER_PAGE = '999999';
export const REQUEST_TIMEOUT_MS = 12000;
export const RETRY_DELAY_MS = 400;
export const MAX_CACHED_REQUESTS = 8;

/** Buttons that open the drawer; hovering, touching or focusing one starts the request early. */
const OPEN_TRIGGER_SELECTOR = '[on\\:click="#filters-drawer/open"], [on\\:click="#filters-drawer/toggle"]';
const INTENT_EVENTS = ['pointerover', 'touchstart', 'focusin'];

/**
 * Content of the filters drawer (`<theme-drawer id="filters-drawer">`) on collection pages.
 *
 * With `data-lazy` the server sends only a loading state. The facets are fetched from the Section Rendering API
 * when the drawer opens (or on intent), stamped once, and morphed on later filter changes. `data-skip-subtree-update`
 * keeps section morphs away from the stamped content, so this element refreshes it itself. Without `data-lazy`
 * (theme editor, search) the content is server-rendered and this element does nothing.
 */
export class FiltersDrawerContent extends HTMLElement {
  /** @type {Map<string, Promise<string>>} Payload requests by URL. */
  #requests = new Map();

  /** @type {string | null} The request key the stamped content was rendered for. */
  #loadedKey = null;

  #stamped = false;

  /** @type {{ key: string, promise: Promise<void> } | null} */
  #pending = null;

  #generation = 0;

  #placeholderHTML = '';

  /** @type {HTMLElement | null} */
  #drawer = null;

  connectedCallback() {
    if (!this.hasAttribute('data-lazy')) return;

    this.#drawer = this.closest('theme-drawer');
    this.#drawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleOpen);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    for (const name of INTENT_EVENTS) {
      document.addEventListener(name, this.#handleIntent, { capture: true, passive: true });
    }

    // The shopper may have opened the drawer before this low-priority module ran.
    if (this.#isOpen) this.ensureContent();
  }

  disconnectedCallback() {
    this.#drawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleOpen);
    this.#drawer = null;
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    for (const name of INTENT_EVENTS) {
      document.removeEventListener(name, this.#handleIntent, { capture: true });
    }
  }

  /**
   * Makes the content match the current URL: stamps it the first time, morphs it afterwards.
   * Concurrent calls for the same URL share one load; a newer URL supersedes an older load.
   *
   * @returns {Promise<void>}
   */
  ensureContent() {
    const key = this.#requestKey();

    if (this.#stamped && this.#loadedKey === key) {
      // Back on the stamped URL (A, B, A): a load still in flight for another URL is now stale.
      if (this.#pending) {
        this.#generation++;
        this.#pending = null;
        this.#release();
      }
      return Promise.resolve();
    }
    if (this.#pending?.key === key) return this.#pending.promise;

    const generation = ++this.#generation;
    const promise = this.#load(key, generation).finally(() => {
      if (this.#pending?.promise === promise) this.#pending = null;
    });
    this.#pending = { key, promise };

    return promise;
  }

  get #isOpen() {
    return this.#drawer?.hasAttribute('open') ?? false;
  }

  /** The Section Rendering URL: current URL (filters and sort included), this section, the sentinel page. */
  #requestKey() {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('section_id', this.getAttribute('section-id') ?? '');
    url.searchParams.set('page', DRAWER_PAGE);
    url.searchParams.sort();

    return url.toString();
  }

  /**
   * @param {string} key
   * @param {number} generation
   */
  async #load(key, generation) {
    if (!this.#stamped) {
      // Back to the loading state (after an error). Keep focus inside the drawer if it was on Try again.
      if (!this.#placeholderHTML) this.#placeholderHTML = this.innerHTML;
      const hadFocus = this.contains(document.activeElement);
      this.innerHTML = this.#placeholderHTML;
      if (hadFocus) this.#focusClose();
    }
    this.setAttribute('aria-busy', 'true');
    // Stamped inputs describe the previous URL until the refresh lands; keep them from being submitted.
    if (this.#stamped) this.setAttribute('inert', '');

    try {
      const html = await this.#fetchPayload(key);
      if (generation !== this.#generation) return;

      this.#apply(html);
      this.#loadedKey = key;
    } catch {
      if (generation !== this.#generation) return;

      // Stale content is better than none: keep it and let the next open retry.
      if (!this.#stamped) this.#renderError();
    } finally {
      if (generation === this.#generation) this.#release();
    }
  }

  /** @param {string} html */
  #apply(html) {
    if (this.#stamped) {
      const next = document.createElement('div');
      next.innerHTML = html;
      morph(this, next);
    } else {
      this.innerHTML = html;
      this.#stamped = true;
    }

    // scroll-hint only recomputes its edge fade on resize or scroll; the content height just changed.
    this.closest('scroll-hint')?.dispatchEvent(new Event('scroll'));
  }

  #release() {
    this.removeAttribute('aria-busy');
    this.removeAttribute('inert');
  }

  #focusClose() {
    /** @type {HTMLElement | null | undefined} */ (this.#drawer?.querySelector('.theme-drawer__close-button'))?.focus();
  }

  #renderError() {
    const status = document.createElement('div');
    status.className = 'gco-filters-drawer__status gco-filters-drawer__status--error';
    status.setAttribute('role', 'alert');

    const message = document.createElement('p');
    message.textContent = this.getAttribute('data-error-text') ?? '';

    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'button button-secondary';
    retry.textContent = this.getAttribute('data-retry-text') ?? '';
    retry.addEventListener('click', (event) => {
      // The load swaps this button out. Left to bubble, the drawer would see a click on a detached node,
      // read it as a click outside the panel and close.
      event.stopPropagation();
      this.ensureContent();
    });

    status.append(message, retry);
    this.replaceChildren(status);

    if (this.#isOpen) retry.focus({ preventScroll: true });
  }

  /**
   * Fetches (or reuses) the payload for a key. Failed requests are never served from the cache.
   *
   * @param {string} key
   * @returns {Promise<string>}
   */
  #fetchPayload(key) {
    const existing = this.#requests.get(key);
    if (existing) return existing;

    const request = this.#request(key);
    this.#requests.set(key, request);

    for (const oldKey of this.#requests.keys()) {
      if (this.#requests.size <= MAX_CACHED_REQUESTS) break;
      this.#requests.delete(oldKey);
    }

    request.catch(() => {
      if (this.#requests.get(key) === request) this.#requests.delete(key);
    });

    return request;
  }

  /**
   * One retry for transient failures, a timeout per attempt.
   *
   * @param {string} key
   * @returns {Promise<string>}
   */
  async #request(key) {
    for (let attempt = 0; ; attempt++) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      try {
        const response = await fetch(key, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Filters drawer request failed with status ${response.status}`);

        const text = await response.text();
        const payload = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-filters-drawer-payload]');
        if (!payload) throw new Error('Filters drawer payload missing from the response');

        return payload.innerHTML;
      } catch (error) {
        if (attempt >= 1) throw error;

        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      } finally {
        clearTimeout(timer);
      }
    }
  }

  /** @param {Event} event */
  #handleOpen = (event) => {
    if (event.target !== this.#drawer) return;
    this.ensureContent();
  };

  /** @param {Event} event */
  #handleIntent = (event) => {
    const target = /** @type {any} */ (event.target);
    if (!target?.closest?.(OPEN_TRIGGER_SELECTOR)) return;

    const key = this.#requestKey();
    if (this.#stamped && this.#loadedKey === key) return;

    this.#fetchPayload(key).catch(() => {});
  };

  /**
   * Filters, sort or the URL changed (facets.js pushes the URL before dispatching). An open drawer refreshes now;
   * a closed one refreshes on its next open, so filtering from elsewhere costs no request.
   *
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {any} */ (event.target)?.closest?.('.shopify-section');
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;
    if (!this.#isOpen) return;

    this.ensureContent();
  };
}

if (!customElements.get('gco-filters-drawer-content')) {
  customElements.define('gco-filters-drawer-content', FiltersDrawerContent);
}
